import { useState } from 'react';
import { Loader2, Sparkles, CheckCircle2, AlertTriangle, Copy, Check } from 'lucide-react';
import Button from './ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import Badge from './ui/Badge';
import api from '../lib/api';

export interface ContentQACategory {
  score: number;
  issues: string[];
  suggestions: string[];
}

export interface ContentQAReport {
  overallScore: number;
  passed: boolean;
  summary: string;
  categories: {
    brandAlignment: ContentQACategory;
    compliance: ContentQACategory;
    grammarTone: ContentQACategory;
    briefAlignment: ContentQACategory;
    captionQuality?: ContentQACategory;
  };
  revisionFeedback: string;
  priorityFixes: string[];
  model?: string;
  cached?: boolean;
  demo?: boolean;
}

interface AIContentQAPanelProps {
  mode: 'script' | 'submission';
  entityId: string;
  title?: string;
  description?: string;
  onUseRevisionFeedback?: (feedback: string) => void;
  className?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  brandAlignment: 'Brand Alignment',
  compliance: 'Compliance',
  grammarTone: 'Grammar & Tone',
  briefAlignment: 'Brief Alignment',
  captionQuality: 'Caption Quality',
};

function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 70) return 'text-amber-600';
  return 'text-red-600';
}

function scoreBadgeVariant(score: number): 'success' | 'warning' | 'destructive' {
  if (score >= 80) return 'success';
  if (score >= 70) return 'warning';
  return 'destructive';
}

export function AIContentQAPanel({
  mode,
  entityId,
  title = 'Content Quality Assurance',
  description = 'AI pre-screen for brand alignment, compliance, and brief fit before human review.',
  onUseRevisionFeedback,
  className = '',
}: AIContentQAPanelProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<ContentQAReport | null>(null);
  const [copied, setCopied] = useState(false);

  const runCheck = async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint =
        mode === 'script'
          ? '/ai/content-qa/script'
          : `/submissions/${entityId}/ai-check`;
      const payload = mode === 'script' ? { scriptId: entityId } : undefined;
      const res = await api.post(endpoint, payload);
      setReport(res.data);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to run quality check';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const copyFeedback = async () => {
    if (!report?.revisionFeedback) return;
    await navigator.clipboard.writeText(report.revisionFeedback);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card className={`border-purple-200/60 bg-purple-50/30 dark:bg-purple-950/20 ${className}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Sparkles className="h-5 w-5 text-purple-600" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={runCheck} disabled={loading} size="sm">
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Run AI Quality Check
              </>
            )}
          </Button>
          {report?.demo && <Badge variant="outline">Demo mode</Badge>}
          {report?.cached && <Badge variant="outline">Cached</Badge>}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {report && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 p-4 rounded-lg border bg-white/60 dark:bg-black/20">
              <div className="flex items-center gap-2">
                {report.passed ? (
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                ) : (
                  <AlertTriangle className="h-6 w-6 text-amber-600" />
                )}
                <span className="font-semibold">
                  {report.passed ? 'Likely ready for review' : 'Revisions recommended'}
                </span>
              </div>
              <Badge variant={scoreBadgeVariant(report.overallScore)}>
                Score: {report.overallScore}/100
              </Badge>
              {report.model && (
                <span className="text-xs text-muted-foreground">Model: {report.model}</span>
              )}
            </div>

            <p className="text-sm text-gray-700 dark:text-gray-300">{report.summary}</p>

            <div className="grid sm:grid-cols-2 gap-3">
              {Object.entries(report.categories).map(([key, cat]) => (
                <div key={key} className="rounded-lg border p-3 bg-white/50 dark:bg-black/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{CATEGORY_LABELS[key] || key}</span>
                    <span className={`text-sm font-bold ${scoreColor(cat.score)}`}>{cat.score}</span>
                  </div>
                  {cat.issues.length > 0 && (
                    <ul className="text-xs text-red-700 dark:text-red-300 space-y-1 mb-2">
                      {cat.issues.map((issue, i) => (
                        <li key={i}>• {issue}</li>
                      ))}
                    </ul>
                  )}
                  {cat.suggestions.length > 0 && (
                    <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                      {cat.suggestions.map((s, i) => (
                        <li key={i}>→ {s}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {report.priorityFixes.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Priority fixes</p>
                <ol className="text-sm space-y-1 list-decimal list-inside text-gray-700 dark:text-gray-300">
                  {report.priorityFixes.map((fix, i) => (
                    <li key={i}>{fix}</li>
                  ))}
                </ol>
              </div>
            )}

            {report.revisionFeedback && (
              <div className="rounded-lg border border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">Suggested revision feedback</p>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={copyFeedback}>
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                    {onUseRevisionFeedback && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onUseRevisionFeedback(report.revisionFeedback)}
                      >
                        Use as revision comment
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-sm whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                  {report.revisionFeedback}
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
